/**
 * Custom Google OAuth callback page (drtoolsuk.com branded flow).
 *
 * Users land here at /auth/google?code=<code>&state=<state> after Google's
 * consent screen. We POST both back to our backend which exchanges the code
 * for tokens against our OWN Google OAuth client (not Emergent's), verifies
 * the id_token, and returns our own session JWT + user.
 *
 * The redirect_uri sent here MUST match the one used in /auth/google/start
 * (Google enforces exact equality) — we rebuild it from window.location.origin.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const GoogleOAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSession } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    // Strict-mode double-invocation guard: the auth code is single-use.
    if (processed.current) return;
    processed.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');

    if (oauthError) {
      setError(`Google returned an error: ${oauthError}. Please try again.`);
      return;
    }
    if (!code || !state) {
      setError('Missing OAuth code / state from Google. Please try signing in again.');
      return;
    }

    (async () => {
      try {
        const redirectUri = window.location.origin + '/auth/google';
        const r = await api.post('/auth/google/callback', { code, state, redirect_uri: redirectUri });
        const { token, user } = r.data || {};
        if (!token || !user) throw new Error('Invalid callback response');
        setSession(token, user);
        // Clear the URL so a refresh doesn't retry the (now-consumed) code.
        window.history.replaceState(null, '', '/');
        toast.success(`Welcome, ${user.name || user.email}`);
        navigate('/account', { replace: true });
      } catch (err) {
        console.error('Google OAuth callback failed:', err);
        setError(err.response?.data?.detail || 'Sign-in failed. Please try again.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      {error ? (
        <>
          <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-[#12263A] mb-2">Sign-in failed</h1>
          <p className="text-neutral-600 mb-6" data-testid="google-oauth-error">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            data-testid="google-oauth-back-btn"
            className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-6 py-3 rounded-sm"
          >
            Back to sign in
          </button>
        </>
      ) : (
        <>
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-orange-50 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-[#F46A25]" />
          </div>
          <h1 className="text-2xl font-black text-[#12263A] mb-2">Signing you in…</h1>
          <p className="text-neutral-600 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Verifying your Google session
          </p>
        </>
      )}
    </div>
  );
};

export default GoogleOAuthCallback;
